// src/admin/AdminCatechistGifts.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { StudentContext } from '../StudentContext';
import { 
  fetchAllCatechists, 
  fetchGiftEvents, 
  saveGiftEvent, 
  deleteGiftEvent,
  fetchAllGiftRegistrations,
  fetchAllUserCustomGifts
} from '../FirebaseService';
import './AdminCatechistGifts.css';

const MONTH_OPTIONS = [
  { value: 'all', label: 'Tất cả các tháng' },
  { value: '1', label: 'Tháng 1' },
  { value: '2', label: 'Tháng 2' },
  { value: '3', label: 'Tháng 3' },
  { value: '4', label: 'Tháng 4' },
  { value: '5', label: 'Tháng 5' },
  { value: '6', label: 'Tháng 6' },
  { value: '7', label: 'Tháng 7' },
  { value: '8', label: 'Tháng 8' },
  { value: '9', label: 'Tháng 9' },
  { value: '10', label: 'Tháng 10' },
  { value: '11', label: 'Tháng 11' },
  { value: '12', label: 'Tháng 12' },
];

// Trích xuất tháng từ chuỗi ngày sinh (hỗ trợ DD/MM/YYYY, YYYY-MM-DD, D/M/YYYY...)
const extractBirthMonth = (dobStr) => {
  if (!dobStr) return null;
  const str = dobStr.toString().trim();
  const dmyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmyMatch) {
    return parseInt(dmyMatch[2], 10);
  }
  const ymdMatch = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (ymdMatch) {
    return parseInt(ymdMatch[2], 10);
  }
  return null;
};

const AdminCatechistGifts = () => {
  const { blocks } = useContext(StudentContext) || {};
  const currentMonth = new Date().getMonth() + 1; // 1 - 12

  // 1. Quản lý Tab Sự kiện Admin đang xem
  const [activeTab, setActiveTab] = useState('birthday'); // 'birthday' | 'event_2010' | custom eventId
  const [eventsList, setEventsList] = useState([]);
  const [loading, setLoading] = useState(true);

  // 2. Dữ liệu Giáo lý viên, Đăng ký quà, và Kho quà tự thêm của GLV
  const [catechists, setCatechists] = useState([]);
  const [registrations, setRegistrations] = useState({}); // { [uid]: regData }
  const [allCustomGiftsMap, setAllCustomGiftsMap] = useState({}); // { [uid]: [customGifts] }
  const [showCustomWishlistModal, setShowCustomWishlistModal] = useState(false);

  // 3. Bộ lọc
  const [selectedMonth, setSelectedMonth] = useState('all'); // Dành cho Sinh nhật
  const [selectedBlock, setSelectedBlock] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all'); // 'all', 'confirmed', 'pending'
  const [selectedGiftType, setSelectedGiftType] = useState('all'); // 'all', 'custom', 'default'
  const [searchKeyword, setSearchKeyword] = useState('');

  // 4. Modal tạo / chỉnh sửa sự kiện khác (Sinh nhật là mặc định)
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventType, setNewEventType] = useState('event_2010');
  const [newEventDeadline, setNewEventDeadline] = useState('');
  const [newEventEndDate, setNewEventEndDate] = useState('');
  const [isSavingEvent, setIsSavingEvent] = useState(false);

  // Tải danh sách GLV và Sự kiện
  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        const [staffList, events, customGifts] = await Promise.all([
          fetchAllCatechists(),
          fetchGiftEvents(),
          fetchAllUserCustomGifts ? fetchAllUserCustomGifts() : Promise.resolve({})
        ]);

        setCatechists(staffList || []);
        if (customGifts) setAllCustomGiftsMap(customGifts);

        // Tab Mặc định Sinh nhật
        const defaultBirthday = {
          id: 'birthday',
          title: '🎂 Quà Sinh Nhật (Mặc Định)',
          type: 'birthday',
          isDefault: true
        };

        const has2010 = events.some(e => e.type === 'event_2010' || e.id === 'event_2010');
        let fullEvents = [defaultBirthday, ...events.filter(e => e.id !== 'birthday')];

        if (!has2010) {
          fullEvents.push({
            id: 'event_2010',
            title: '💐 Mừng 20/10 (Phụ Nữ VN)',
            type: 'event_2010',
            deadline: '2026-10-20T23:59:59',
            endDate: '2026-10-31T23:59:59',
            description: 'Sự kiện mừng ngày Phụ Nữ Việt Nam 20/10'
          });
        }

        setEventsList(fullEvents);
      } catch (err) {
        console.error('Lỗi nạp dữ liệu:', err);
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, []);

  // Tải đăng ký quà của sự kiện đang chọn
  useEffect(() => {
    const loadRegs = async () => {
      try {
        const regs = await fetchAllGiftRegistrations(activeTab);
        setRegistrations(regs || {});
      } catch (err) {
        console.error('Lỗi tải đăng ký quà:', err);
        setRegistrations({});
      }
    };

    loadRegs();
  }, [activeTab]);

  const currentEvent = useMemo(() => {
    return eventsList.find(e => e.id === activeTab) || eventsList[0] || {
      id: 'birthday',
      title: '🎂 Quà Sinh Nhật',
      type: 'birthday'
    };
  }, [eventsList, activeTab]);

  const blockOptions = useMemo(() => {
    if (!blocks) return [];
    return Object.keys(blocks);
  }, [blocks]);

  // Ghép nối danh sách GLV với lựa chọn quà
  const combinedList = useMemo(() => {
    return catechists.map(cat => {
      const reg = registrations[cat.uid] || null;
      const bMonth = extractBirthMonth(cat.dateOfBirth || cat.birthDate);
      const userCustomGifts = allCustomGiftsMap[cat.uid] || [];

      return {
        uid: cat.uid,
        holyName: cat.holyName || '',
        fullName: cat.fullName || '',
        dateOfBirth: cat.dateOfBirth || cat.birthDate || 'Chưa cập nhật',
        birthMonth: bMonth,
        gender: cat.gender || 'Nam',
        blockGroup: cat.blockLeader || cat.blockGroup || '',
        className: cat.className || '',
        isRegistered: Boolean(reg),
        giftName: reg?.giftName || '',
        giftImage: reg?.giftImage || '',
        isCustom: Boolean(reg?.isCustom),
        purchaseUrl: reg?.purchaseUrl || '',
        tag: reg?.tag || '',
        wishTitle: reg?.wishTitle || '',
        wishContent: reg?.wishContent || '',
        confirmedAt: reg?.confirmedAt || '',
        userCustomCount: userCustomGifts.length,
        userCustomList: userCustomGifts
      };
    });
  }, [catechists, registrations, allCustomGiftsMap]);

  // Lọc dữ liệu theo bộ lọc
  const filteredList = useMemo(() => {
    return combinedList.filter(item => {
      // 1. Lọc theo tháng sinh (Chỉ kích hoạt khi ở Tab Sinh Nhật)
      if (activeTab === 'birthday' && selectedMonth !== 'all') {
        if (item.birthMonth !== parseInt(selectedMonth, 10)) {
          return false;
        }
      }

      // 2. Lọc theo Khối
      if (selectedBlock !== 'all') {
        const matchBlock = (item.blockGroup && item.blockGroup.includes(selectedBlock)) ||
                           (item.className && item.className.includes(selectedBlock));
        if (!matchBlock) return false;
      }

      // 3. Lọc theo trạng thái chốt quà
      if (selectedStatus === 'confirmed' && !item.isRegistered) return false;
      if (selectedStatus === 'pending' && item.isRegistered) return false;

      // 4. Lọc theo loại quà (Tự thêm / Mặc định)
      if (selectedGiftType === 'custom' && (!item.isRegistered || !item.isCustom)) return false;
      if (selectedGiftType === 'default' && (!item.isRegistered || item.isCustom)) return false;

      // 5. Tìm kiếm từ khóa (Tên Thánh, Họ tên, Tên món quà, Link mua hàng)
      if (searchKeyword.trim()) {
        const kw = searchKeyword.toLowerCase();
        const matchName = item.fullName.toLowerCase().includes(kw);
        const matchHoly = item.holyName.toLowerCase().includes(kw);
        const matchGift = item.giftName.toLowerCase().includes(kw);
        const matchUrl = item.purchaseUrl.toLowerCase().includes(kw);
        if (!matchName && !matchHoly && !matchGift && !matchUrl) return false;
      }

      return true;
    });
  }, [combinedList, activeTab, selectedMonth, selectedBlock, selectedStatus, selectedGiftType, searchKeyword]);

  // 🌟 THỐNG KÊ GOM NHÓM (GROUP AGGREGATION) CHO HẬU CẦN
  const aggregatedGifts = useMemo(() => {
    const map = {};
    filteredList.forEach(item => {
      if (item.isRegistered && item.giftName) {
        if (!map[item.giftName]) {
          map[item.giftName] = {
            name: item.giftName,
            count: 0,
            isCustom: item.isCustom,
            tag: item.tag || '',
            image: item.giftImage,
            purchaseUrls: [],
            buyers: []
          };
        }
        map[item.giftName].count += 1;
        if (item.purchaseUrl && !map[item.giftName].purchaseUrls.includes(item.purchaseUrl)) {
          map[item.giftName].purchaseUrls.push(item.purchaseUrl);
        }
        map[item.giftName].buyers.push(`${item.holyName} ${item.fullName}`.trim());
      }
    });

    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [filteredList]);

  // Thống kê tổng số
  const stats = useMemo(() => {
    const totalGLV = filteredList.length;
    const confirmedCount = filteredList.filter(i => i.isRegistered).length;
    const pendingCount = totalGLV - confirmedCount;
    const customGiftCount = filteredList.filter(i => i.isRegistered && i.isCustom).length;

    return { totalGLV, confirmedCount, pendingCount, customGiftCount };
  }, [filteredList]);

  // Tổng hợp tất cả các món quà tự thêm của toàn bộ GLV (dành cho modal tham khảo)
  const allCustomGiftsList = useMemo(() => {
    const list = [];
    combinedList.forEach(cat => {
      if (cat.userCustomList && cat.userCustomList.length > 0) {
        cat.userCustomList.forEach(g => {
          list.push({
            ...g,
            ownerHoly: cat.holyName,
            ownerName: cat.fullName,
            ownerClass: cat.className || cat.blockGroup
          });
        });
      }
    });
    return list;
  }, [combinedList]);

  // Xuất file CSV: Thống kê Gom nhóm Mua quà (Kèm Link mua hàng)
  const exportGroupedPurchasesCSV = () => {
    if (aggregatedGifts.length === 0) {
      alert('Không có dữ liệu quà đã chốt để xuất!');
      return;
    }

    let csvContent = '\uFEFFSTT,Tên Món Quà,Loại Quà,Số Lượng Cần Mua,Link Mua Hàng,Danh Sách Người Chọn\n';
    aggregatedGifts.forEach((g, idx) => {
      const typeStr = g.isCustom ? 'Quà Tự Thêm' : 'Quà Mặc Định';
      const linkStr = `"${g.purchaseUrls.join(' | ')}"`;
      const buyerStr = `"${g.buyers.join('; ')}"`;
      csvContent += `${idx + 1},"${g.name}","${typeStr}",${g.count},${linkStr},${buyerStr}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DS_Mua_Qua_Gom_Nhom_${activeTab}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Xuất file CSV: Danh sách Chi tiết Trao quà (Kèm Link mua hàng)
  const exportDetailedGiftListCSV = () => {
    if (filteredList.length === 0) {
      alert('Không có dữ liệu để xuất!');
      return;
    }

    let csvContent = '\uFEFFSTT,Tên Thánh,Họ và Tên,Ngày Sinh,Khối/Lớp,Trạng Thái,Món Quà Đã Chọn,Loại Quà,Link Mua Hàng,Thời Điểm Chốt\n';
    filteredList.forEach((item, idx) => {
      const statusText = item.isRegistered ? 'Đã Chốt' : 'Chưa Chọn';
      const typeText = item.isRegistered ? (item.isCustom ? 'Tự Thêm' : 'Mặc Định') : '—';
      const confirmedTime = item.confirmedAt ? new Date(item.confirmedAt).toLocaleDateString('vi-VN') : '';
      const linkText = item.purchaseUrl ? `"${item.purchaseUrl}"` : '""';
      csvContent += `${idx + 1},"${item.holyName}","${item.fullName}","${item.dateOfBirth}","${item.className || item.blockGroup}","${statusText}","${item.giftName}","${typeText}",${linkText},"${confirmedTime}"\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DS_Chi_Tiet_Trao_Qua_${activeTab}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Thêm sự kiện mới (cho 20/10 hoặc các dịp khác)
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    if (!newEventTitle.trim() || isSavingEvent) return;

    setIsSavingEvent(true);
    const eventId = newEventType === 'event_2010' ? 'event_2010' : `event_${Date.now()}`;
    const payload = {
      id: eventId,
      title: newEventTitle.trim(),
      type: newEventType,
      deadline: newEventDeadline ? new Date(newEventDeadline).toISOString() : '',
      endDate: newEventEndDate ? new Date(newEventEndDate).toISOString() : '',
      createdAt: new Date().toISOString()
    };

    const res = await saveGiftEvent(payload);
    if (res.success) {
      setEventsList(prev => [...prev.filter(ev => ev.id !== eventId), payload]);
      setActiveTab(eventId);
      setShowEventModal(false);
      setNewEventTitle('');
      setNewEventDeadline('');
      setNewEventEndDate('');
    } else {
      alert('Lỗi lưu sự kiện: ' + res.message);
    }
    setIsSavingEvent(false);
  };

  return (
    <div className="admin-gifts-page">
      {/* 1. THANH TABS CHUYỂN SỰ KIỆN */}
      <div className="admin-gifts-tabs-bar">
        <div className="events-tabs-list">
          {eventsList.map(ev => (
            <button
              key={ev.id}
              type="button"
              className={`event-nav-btn ${activeTab === ev.id ? 'active' : ''}`}
              onClick={() => setActiveTab(ev.id)}
            >
              {ev.title}
            </button>
          ))}
        </div>

        <div className="top-action-buttons">
          <button
            type="button"
            className="btn-view-custom-wishlist"
            onClick={() => setShowCustomWishlistModal(true)}
            title="Xem danh sách tất cả các món quà do các Giáo lý viên tự thêm vào hệ thống"
          >
            💡 Kho Quà GLV Tự Thêm ({allCustomGiftsList.length})
          </button>

          <button
            type="button"
            className="btn-add-event"
            onClick={() => setShowEventModal(true)}
          >
            ➕ Thêm Sự Kiện Mới
          </button>
        </div>
      </div>

      {/* 2. DASHBOARD THỐNG KÊ TỔNG QUAN */}
      <div className="admin-gifts-stats-grid">
        <div className="stat-box box-total">
          <span className="stat-label">Tổng GLV (Bộ Lọc)</span>
          <strong className="stat-val">{stats.totalGLV}</strong>
        </div>
        <div className="stat-box box-confirmed">
          <span className="stat-label">Đã Chốt Quà</span>
          <strong className="stat-val">{stats.confirmedCount}</strong>
        </div>
        <div className="stat-box box-pending">
          <span className="stat-label">Chưa Chọn Quà</span>
          <strong className="stat-val">{stats.pendingCount}</strong>
        </div>
        <div className="stat-box box-custom">
          <span className="stat-label">Chọn Quà Tự Thêm</span>
          <strong className="stat-val">{stats.customGiftCount}</strong>
        </div>
      </div>

      {/* 3. BỘ LỌC TÌM KIẾM & XUẤT FILE */}
      <div className="admin-gifts-toolbar">
        <div className="filters-group">
          {/* 🌟 BỘ LỌC THÁNG SINH (CHỈ HIỂN THỊ KHI Ở TAB SINH NHẬT) */}
          {activeTab === 'birthday' && (
            <div className="filter-item month-filter-wrap">
              <label>📅 Tháng Sinh:</label>
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                className="admin-select"
              >
                {MONTH_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <button
                type="button"
                className={`btn-current-month ${selectedMonth === String(currentMonth) ? 'active' : ''}`}
                onClick={() => setSelectedMonth(String(currentMonth))}
              >
                Tháng Này ({currentMonth})
              </button>
            </div>
          )}

          {/* Lọc Khối */}
          <div className="filter-item">
            <label>🏫 Khối:</label>
            <select
              value={selectedBlock}
              onChange={e => setSelectedBlock(e.target.value)}
              className="admin-select"
            >
              <option value="all">Tất cả Khối</option>
              {blockOptions.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          {/* Lọc Trạng thái */}
          <div className="filter-item">
            <label>🎯 Trạng thái:</label>
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              className="admin-select"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="confirmed">Đã chốt quà ({stats.confirmedCount})</option>
              <option value="pending">Chưa chọn quà ({stats.pendingCount})</option>
            </select>
          </div>

          {/* Lọc Loại quà */}
          <div className="filter-item">
            <label>🎁 Nguồn Quà:</label>
            <select
              value={selectedGiftType}
              onChange={e => setSelectedGiftType(e.target.value)}
              className="admin-select"
            >
              <option value="all">Tất cả quà</option>
              <option value="custom">Quà do GLV tự thêm</option>
              <option value="default">Quà mẫu mặc định</option>
            </select>
          </div>

          {/* Ô tìm kiếm */}
          <div className="filter-item search-wrap">
            <input
              type="text"
              placeholder="🔍 Tìm Tên Thánh, Họ tên, Tên món, Link mua..."
              value={searchKeyword}
              onChange={e => setSearchKeyword(e.target.value)}
              className="admin-search-input"
            />
          </div>
        </div>

        {/* CÁC NÚT XUẤT FILE HẬU CẦN */}
        <div className="export-actions-group">
          <button
            type="button"
            className="btn-export-group"
            onClick={exportGroupedPurchasesCSV}
            title="Xuất file tổng hợp số lượng từng món để đi mua"
          >
            📦 Xuất Gom Nhóm Mua Quà
          </button>
          <button
            type="button"
            className="btn-export-detail"
            onClick={exportDetailedGiftListCSV}
            title="Xuất file danh sách GLV và món quà để trao"
          >
            📋 Xuất DS Chi Tiết Trao Quà
          </button>
        </div>
      </div>

      {/* 4. PHẦN HẬU CẦN: BẢNG GOM NHÓM SỐ LƯỢNG MÓN QUÀ (AGGREGATION TABLE) */}
      <div className="admin-group-aggregation-card">
        <div className="card-header-row">
          <h3>📦 Thống Kê Gom Nhóm Số Lượng Quà Cần Mua ({aggregatedGifts.length} món)</h3>
          <span className="card-hint">
            Tự động tổng hợp số lượng từng món và đính kèm link mua hàng trực tiếp để Admin tiện đặt mua
          </span>
        </div>

        {aggregatedGifts.length === 0 ? (
          <div className="no-aggregate-hint">Chưa có ai chốt quà trong phạm vi bộ lọc này.</div>
        ) : (
          <div className="aggregation-grid">
            {aggregatedGifts.map((g, idx) => (
              <div key={idx} className={`aggregate-item-card ${g.isCustom ? 'is-custom-gift' : ''}`}>
                <div className="agg-top">
                  <span className="agg-badge-count">SL: <strong>{g.count}</strong></span>
                  {g.isCustom ? (
                    <span className="agg-badge-type custom">✨ Quà tự thêm</span>
                  ) : (
                    <span className="agg-badge-type default">🎁 Quà có sẵn</span>
                  )}
                </div>

                <h4 className="agg-name">{g.name}</h4>

                {/* LINK MUA SẢN PHẨM */}
                {g.purchaseUrls && g.purchaseUrls.length > 0 ? (
                  <div className="agg-links-wrap">
                    <span className="link-title">🔗 Link mua hàng:</span>
                    {g.purchaseUrls.map((url, uIdx) => (
                      <a
                        key={uIdx}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agg-purchase-link-btn"
                        title={url}
                      >
                        🛒 Mở link đặt mua {g.purchaseUrls.length > 1 ? `#${uIdx + 1}` : ''}
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="agg-no-link">Không có link sản phẩm</div>
                )}

                <div className="agg-buyers-list">
                  <small>Người chọn ({g.buyers.length}):</small>
                  <p>{g.buyers.join(', ')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 5. BẢNG DANH SÁCH CHI TIẾT GLV */}
      <div className="admin-gifts-table-wrap">
        <div className="card-header-row">
          <h3>📋 Danh Sách Chi Tiết Giáo Lý Viên ({filteredList.length})</h3>
        </div>

        <table className="admin-gifts-table">
          <thead>
            <tr>
              <th>STT</th>
              <th>Tên Thánh & Họ Tên</th>
              <th>Ngày Sinh</th>
              <th>Khối / Lớp</th>
              <th>Trạng Thái</th>
              <th>Món Quà Đã Chọn</th>
              <th>Nguồn Quà</th>
              <th>Link Mua Hàng</th>
              <th>Thời Điểm Chốt</th>
              <th>Lời Chúc Đi Kèm</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-row">Không tìm thấy Giáo lý viên nào phù hợp bộ lọc.</td>
              </tr>
            ) : (
              filteredList.map((item, idx) => (
                <tr key={item.uid} className={item.isRegistered ? 'row-confirmed' : 'row-pending'}>
                  <td className="text-center">{idx + 1}</td>
                  <td>
                    <strong>{item.holyName} {item.fullName}</strong>
                  </td>
                  <td>
                    <span className="dob-badge">
                      🎂 {item.dateOfBirth}
                    </span>
                  </td>
                  <td>{item.className || item.blockGroup || 'Chưa phân'}</td>
                  <td className="text-center">
                    {item.isRegistered ? (
                      <span className="chip-confirmed">✓ Đã Chốt</span>
                    ) : (
                      <span className="chip-pending">⏳ Chưa chọn</span>
                    )}
                  </td>
                  <td>
                    {item.isRegistered ? (
                      <div className="gift-cell">
                        {item.giftImage && (
                          <img src={item.giftImage} alt="" className="table-gift-thumb" />
                        )}
                        <span className="table-gift-name">{item.giftName}</span>
                      </div>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-center">
                    {item.isRegistered ? (
                      item.isCustom ? (
                        <span className="badge-gift-source custom">✨ Tự thêm</span>
                      ) : (
                        <span className="badge-gift-source default">🎁 Mặc định</span>
                      )
                    ) : '—'}
                  </td>
                  <td>
                    {item.purchaseUrl ? (
                      <a
                        href={item.purchaseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="table-link-btn"
                        title={item.purchaseUrl}
                      >
                        🛒 Mở link mua
                      </a>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {item.confirmedAt ? new Date(item.confirmedAt).toLocaleDateString('vi-VN') : '—'}
                  </td>
                  <td>
                    {item.wishContent ? (
                      <small className="wish-snippet" title={item.wishContent}>
                        "{item.wishContent.substring(0, 45)}..."
                      </small>
                    ) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 6. MODAL XEM KHO QUÀ DO CÁC GLV TỰ THÊM VÀO */}
      {showCustomWishlistModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCustomWishlistModal(false)}>
          <div className="admin-modal-card custom-wishlist-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>💡 Kho Quà Giáo Lý Viên Tự Thêm ({allCustomGiftsList.length} món)</h3>
              <button className="btn-close" onClick={() => setShowCustomWishlistModal(false)}>✕</button>
            </div>

            <p className="modal-hint">
              Danh sách tổng hợp các món quà cá nhân mà các Giáo lý viên đã tự lưu vào hệ thống kèm link tham khảo.
            </p>

            <div className="custom-wishlist-scroll">
              {allCustomGiftsList.length === 0 ? (
                <div className="empty-row">Chưa có Giáo lý viên nào thêm món quà riêng.</div>
              ) : (
                <div className="custom-gifts-admin-grid">
                  {allCustomGiftsList.map((g, idx) => (
                    <div key={idx} className="custom-gift-admin-card">
                      <div className="custom-gift-header">
                        <span className="owner-chip">
                          👤 {g.ownerHoly} {g.ownerName} ({g.ownerClass})
                        </span>
                        <span className="tag-chip">{g.tag}</span>
                      </div>
                      <h4 className="gift-title">{g.name}</h4>
                      {g.purchaseUrl ? (
                        <a
                          href={g.purchaseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="buy-link-pill"
                        >
                          🔗 Mở link mua hàng
                        </a>
                      ) : (
                        <span className="no-link-text">Không có link đính kèm</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-done"
                onClick={() => setShowCustomWishlistModal(false)}
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 7. MODAL THÊM SỰ KIỆN MỚI */}
      {showEventModal && (
        <div className="admin-modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="admin-modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Thêm Sự Kiện Quà Tặng Mới</h3>
              <button className="btn-close" onClick={() => setShowEventModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSaveEvent} className="event-form">
              <div className="form-group">
                <label>Tên Sự Kiện:</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Mừng Ngày 20/10, Quà Giáng Sinh..."
                  value={newEventTitle}
                  onChange={e => setNewEventTitle(e.target.value)}
                  className="admin-input"
                  required
                />
              </div>

              <div className="form-group">
                <label>Loại sự kiện:</label>
                <select
                  value={newEventType}
                  onChange={e => setNewEventType(e.target.value)}
                  className="admin-select"
                >
                  <option value="event_2010">Mừng 20/10 (Phụ Nữ VN)</option>
                  <option value="general">Sự kiện chung / Dịp lễ</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>⏰ Hạn Chót Chọn Quà (Deadline):</label>
                  <input
                    type="datetime-local"
                    value={newEventDeadline}
                    onChange={e => setNewEventDeadline(e.target.value)}
                    className="admin-input"
                    required
                  />
                  <small>Sau hạn này, GLV không được quay hoặc sửa quà.</small>
                </div>

                <div className="form-group">
                  <label>🚫 Ngày Kết Thúc (Ẩn Tab):</label>
                  <input
                    type="datetime-local"
                    value={newEventEndDate}
                    onChange={e => setNewEventEndDate(e.target.value)}
                    className="admin-input"
                    required
                  />
                  <small>Sau ngày này, tab sẽ tự động ẩn khỏi GLV.</small>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowEventModal(false)}
                >
                  Hủy Bỏ
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isSavingEvent}
                >
                  {isSavingEvent ? 'Đang lưu...' : 'Lưu Sự Kiện'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminCatechistGifts;